/**
 * How focus last moved: keyboard or pointer, as `data-modality` on <html>.
 *
 * Text boxes match :focus-visible however they were focused, so CSS alone cannot tell a click into the message
 * box from a Tab into it. The message box (Slack-like, Nathan 20 Oct 2026) keeps the same look on a click and
 * shows a firmer hairline only when focus came from the keyboard (WCAG 2.4.7). Every other control keeps its own
 * :focus-visible ring and does not read this.
 */
export type Modality = 'keyboard' | 'pointer';

/** Keys that move focus or work a control. Typing letters into a box that already has focus is not navigation. */
const NAVIGATION_KEYS = new Set(['Tab', 'Escape', 'Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'F6']);

export function modalityForKey(e: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey'>): Modality | null {
  // Shortcuts such as ⌘K open things and move focus from the keyboard.
  if (e.metaKey || e.ctrlKey || e.altKey) return 'keyboard';
  return NAVIGATION_KEYS.has(e.key) ? 'keyboard' : null;
}

const CAPTURE: AddEventListenerOptions = { capture: true };
type Listeners = Pick<Window, 'addEventListener' | 'removeEventListener'>;

export function trackInputModality(root: { dataset: DOMStringMap } = document.documentElement, target: Listeners = window): () => void {
  const set = (m: Modality) => {
    if (root.dataset.modality !== m) root.dataset.modality = m;
  };
  const onKey = (e: KeyboardEvent) => {
    const m = modalityForKey(e);
    if (m) set(m);
  };
  const onPointer = () => set('pointer');
  target.addEventListener('keydown', onKey as EventListener, CAPTURE);
  target.addEventListener('pointerdown', onPointer, CAPTURE);
  return () => {
    target.removeEventListener('keydown', onKey as EventListener, CAPTURE);
    target.removeEventListener('pointerdown', onPointer, CAPTURE);
  };
}
